import React from 'react';
import { Document } from '@react-pdf/renderer';
import { CoverPage } from './components/CoverPage';
import { RecordSummary } from './DetailedReport';
import { getTypeColor } from './pdfColors';
import { CombinedReportData } from './combinedTypes';
import { DetailedPDFRecord } from './detailedTypes';
import i18n from '../../i18n';

function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key });
}

/** Clave de color del informe (getTypeColor) según el tipo de cada hija. */
const CHILD_TYPE_COLOR_KEY: Record<DetailedPDFRecord['type'], string> = {
  action: 'Action',
  accident: 'Accident',
  communication: 'Communication',
};

/**
 * Documento PDF combinado: portada + página de la Order padre + una página por
 * cada hija, en UN único hilo cronológico ENTRELAZADO (actuaciones, accidentes
 * y comunicaciones mezcladas según el orden elegido en el modal) — sin bloques
 * fijos por tipo; la plantilla de cada página se despacha por `record.type`.
 * Reutiliza `RecordSummary` del informe detallado (acepta `typeColor` por prop y
 * omite la sección de imágenes cuando `images` está vacío → comunicaciones).
 */
export const CombinedReport = ({ data }: { data: CombinedReportData }) => {
  const parentColor = getTypeColor('Generic');
  const total =
    data.counts.actions + data.counts.accidents + data.counts.communications;

  // Desglose de hijas mostrado en la portada (reutiliza el slot filtersSummary)
  const breakdown = [
    {
      label: t('navigation:routes.actions', 'Actuacions'),
      value: String(data.counts.actions),
    },
    {
      label: t('navigation:routes.accidents', 'Accidents'),
      value: String(data.counts.accidents),
    },
    {
      label: t('navigation:routes.communications', 'Comunicacions'),
      value: String(data.counts.communications),
    },
  ];

  // La Order padre se renderiza como un record sin campos de acción → solo
  // muestra info básica, descripción, tablas propias (tramos del RV) y sus
  // imágenes.
  const parentRecord: DetailedPDFRecord = {
    roid: data.parent.roid,
    type: 'action',
    state: data.parent.state,
    basicInfo: data.parent.basicInfo,
    description: data.parent.description ?? '',
    notes: data.parent.notes,
    extraTables: data.parent.extraTables,
    images: data.parent.images,
  };

  return (
    <Document>
      <CoverPage
        title={data.title}
        typeColor={parentColor}
        generatedAt={data.generatedAt}
        totalCount={total}
        filtersSummary={breakdown}
      />
      <RecordSummary
        record={parentRecord}
        typeColor={parentColor}
        title={data.title}
      />
      {data.children.map((r, i) => (
        <RecordSummary
          key={`${r.type}-${i}`}
          record={r}
          typeColor={getTypeColor(CHILD_TYPE_COLOR_KEY[r.type])}
          title={data.title}
        />
      ))}
    </Document>
  );
};
